import { FC } from 'react'
import { Lock } from 'lucide-react'
import { cn } from '../../../lib/utils'

/**
 * Agent persona metadata for visual identity in the case panel.
 * Matches the JSX config/agents.js PERSONAS structure.
 * Local (not exported): consumers use getAgentPersona()/AGENT_PERSONAS values,
 * not the bare type, so exporting it leaves a dead export (knip).
 */
interface AgentPersona {
  id: string
  name: string
  role: string
  avatar: string
  accentFrom: string
  accentTo: string
}

/**
 * Data-driven agent configuration. Add new agents here to make them
 * available throughout the case panel (e.g. Flow Chat).
 */
const AGENT_CONFIG: AgentPersona[] = [
  {
    id: 'casey',
    name: 'Casey',
    role: 'Attending',
    avatar: 'C',
    accentFrom: 'from-blue-500',
    accentTo: 'to-cyan-400'
  },
  {
    id: 'hpi-pe-agent',
    name: 'The Historian',
    role: 'Intern',
    avatar: 'H',
    accentFrom: 'from-emerald-500',
    accentTo: 'to-teal-400'
  },
  {
    id: 'clinical-reasoning',
    name: 'The Resident',
    role: 'Senior Resident',
    avatar: 'R',
    accentFrom: 'from-amber-500',
    accentTo: 'to-orange-400'
  },
  {
    id: 'flow-chat',
    name: 'Flow Chat',
    role: 'Quick Flow',
    avatar: 'FC',
    accentFrom: 'from-sky-500',
    accentTo: 'to-blue-400'
  }
]

export const AGENT_PERSONAS: Record<string, AgentPersona> = Object.fromEntries(
  AGENT_CONFIG.map(agent => [agent.id, agent])
)

const NEUTRAL_PERSONA: AgentPersona = {
  id: 'consultant', name: 'Consultant', role: 'Consult', avatar: '?',
  accentFrom: 'from-slate-500', accentTo: 'to-slate-400',
}

export const getAgentPersona = (agentId: string): AgentPersona => {
  const p = AGENT_PERSONAS[agentId]
  if (!p) {
    console.warn(`[getAgentPersona] unknown agent id "${agentId}" — using neutral Consultant persona`)
    return NEUTRAL_PERSONA
  }
  return p
}

interface AgentBadge {
  tab: string
  status: 'pending' | 'complete'
}

interface AgentSelectorProps {
  agents: Record<string, AgentPersona>
  activeAgentId: string
  onSelect: (agentId: string) => void
  badges?: Record<string, AgentBadge>
  hideFlowChat?: boolean
  flowChatReadOnly?: boolean
}

const getDotColor = (agentId: string): string => {
  switch (agentId) {
    case 'casey': return 'bg-blue-400'
    case 'hpi-pe-agent': return 'bg-emerald-400'
    case 'clinical-reasoning': return 'bg-amber-400'
    case 'flow-chat': return 'bg-sky-400'
    default: return 'bg-blue-400'
  }
}

/**
 * Segmented tab selector for switching between agents in the case panel.
 * Compact 24px inline pills with colored dot indicators.
 */
export const AgentSelector: FC<AgentSelectorProps> = ({
  agents,
  activeAgentId,
  onSelect,
  badges = {},
  hideFlowChat = false,
  flowChatReadOnly = false,
}) => {
  const agentList = Object.values(agents).filter(agent => {
    if (agent.id === 'flow-chat' && hideFlowChat) return false
    return true
  })

  return (
    <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5 border border-border/50">
      {agentList.map(agent => {
        const isActive = agent.id === activeAgentId
        const hasBadge = badges[agent.id]?.status === 'pending'
        const dotColor = getDotColor(agent.id)
        const isFlowChatReadOnly = agent.id === 'flow-chat' && flowChatReadOnly

        return (
          <button
            key={agent.id}
            onClick={() => onSelect(agent.id)}
            className={cn(
              'relative flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-md transition-all duration-200 whitespace-nowrap',
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
              isFlowChatReadOnly && 'opacity-60'
            )}
          >
            <span className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', dotColor, !isActive && 'opacity-50')} />
            <span className="text-[11px]">{agent.name}</span>
            {isFlowChatReadOnly && (
              <Lock className="w-2.5 h-2.5 flex-shrink-0 ml-0.5" />
            )}
            {hasBadge && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 border border-muted animate-pulse" />
            )}
          </button>
        )
      })}
    </div>
  )
}
